import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { useApi, type LeadRow, type LeadsResponse, type MeResponse } from '../lib/api';

// =============================================================================
// Helpers shared by both Leads (top-level page) and the per-client view used
// from /admin/clients/:id.
// =============================================================================

// HTML-escape so the post text + author + summary can't inject markup.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Same approach the extension uses (matches.js): escape the text, then for
// each matched keyword, wrap every case-insensitive occurrence in <mark>.
// Sort keywords by length DESC so "google ads" gets matched before "google"
// (otherwise the inner span would consume the longer phrase's prefix).
export function highlightKeywords(text: string, keywords: string[]): string {
  let html = escapeHtml(text);
  if (!keywords || keywords.length === 0) return html;
  const sorted = [...keywords]
    .filter((k) => typeof k === 'string' && k.length > 0)
    .sort((a, b) => b.length - a.length);
  for (const kw of sorted) {
    const safe = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${safe})`, 'gi');
    html = html.replace(re, '<mark>$1</mark>');
  }
  return html;
}

function formatRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(ms).toLocaleDateString();
}

// Slim subset of /admin/clients we use for the dropdown + per-client stat
// cards on the Leads page. Pulled fresh so a brand-new client appears in
// the picker immediately, without waiting on a Dashboard refresh.
type ClientPickerRow = {
  id: string;
  name: string;
  contact_email: string;
  total_leads: number;
  buyer_intent_leads: number;
  new_leads: number;
  won_leads: number;
  // v3.14 — server-side counts so tab badges show TRUE totals (not
  // just "how many are in the currently-loaded batch"). Fixes the
  // reported "499 total but tabs say 12" mismatch.
  outreached_leads?: number;
  saved_leads?: number;
};

const ALL_CLIENTS = '__all__';

// How many leads show per page. The server still streams leads in larger
// cursor-based batches behind the scenes; this is purely the display page
// size. When the user pages past what's loaded, the next batch is fetched.
const PAGE_SIZE = 10;

export function Leads() {
  const { request } = useApi();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  // Current page (0-indexed) for the client-side pagination over visibleLeads.
  const [page, setPage] = useState(0);

  // Client picker — owner / va_admin only. The picker stays hidden for
  // other roles since they don't have multi-client scope.
  const [clientRows, setClientRows] = useState<ClientPickerRow[] | null>(null);
  const [pickedClient, setPickedClient] = useState<string>(ALL_CLIENTS);

  // Platform filter — driven by the ?platform= query param so the dashboard's
  // platform cards can deep-link straight to a filtered Leads view. Filtering
  // is client-side over the already-loaded page.
  const [searchParams, setSearchParams] = useSearchParams();
  const platformFilter = searchParams.get('platform'); // 'facebook' | 'reddit' | null
  const clearPlatformFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('platform');
    setSearchParams(next, { replace: true });
  };
  // Category quick-filter tabs. 'all'/'new'/'buyer_intent'/'saved'/'won' filter
  // the loaded page client-side. 'archived' is special: archived leads are
  // excluded from the default API response, so selecting it triggers a refetch
  // with ?archived=1 (see loadPage + archivedView below).
  const [category, setCategory] = useState<
    'all' | 'new' | 'buyer_intent' | 'saved' | 'outreached' | 'won' | 'archived'
  >('all');
  const archivedView = category === 'archived';

  const visibleLeads = leads.filter((l) => {
    if (platformFilter && (l.platform ?? 'facebook') !== platformFilter) {
      return false;
    }
    switch (category) {
      case 'new':
        return (l.status ?? 'new') === 'new';
      case 'buyer_intent':
        return l.intent === 'buyer_intent';
      case 'saved':
        return l.saved === 1;
      case 'outreached':
        // v3.14 — "outreached" matches EITHER a status='outreached' OR
        // the older outreached=1 flag (both were used historically).
        return l.status === 'outreached' || l.outreached === 1;
      case 'won':
        return (l.status ?? '') === 'won';
      default:
        return true;
    }
  });

  // v3.14 — tab badges now show the SERVER TOTAL for each category
  // instead of the loaded-batch count. Fixes the "499 total but tabs
  // say 12" mismatch users reported — the old client-side count only
  // reflected the first 50-lead page the API had returned, so any tab
  // was hard-capped at 50. The server counts come from `counts`
  // (aggregated from /admin/clients per-client stats).
  //
  // The one exception: when a platform filter is set (?platform=facebook),
  // we fall back to loaded-batch counts since the server counts don't
  // yet break down by platform. This will still under-count if there
  // are more matching leads beyond the loaded page — indicated by a
  // trailing "+" in the tab label render below.
  const platformScoped = platformFilter
    ? leads.filter((l) => (l.platform ?? 'facebook') === platformFilter)
    : leads;

  // v3.14 — Server-count source for tab badges. Compute inline here so
  // categoryCounts doesn't depend on the later `counts` const (which
  // would trip TypeScript's temporal-dead-zone check). Same logic as
  // the counts memo below — kept in sync manually. If clientRows exists
  // we can derive per-client + agency-wide totals; otherwise null and
  // we fall back to loaded-batch counts (only place a platform filter
  // matters since server counts don't yet split by platform).
  const serverCounts: {
    total: number;
    new_leads: number;
    buyer_intent: number;
    won_leads: number;
    outreached_leads: number;
    saved_leads: number;
  } | null = (() => {
    if (!clientRows || clientRows.length === 0) return null;
    if (pickedClient !== ALL_CLIENTS) {
      const row = clientRows.find((r) => r.id === pickedClient);
      if (!row) return null;
      return {
        total: row.total_leads,
        new_leads: row.new_leads,
        buyer_intent: row.buyer_intent_leads,
        won_leads: row.won_leads,
        outreached_leads: row.outreached_leads ?? 0,
        saved_leads: row.saved_leads ?? 0,
      };
    }
    return clientRows.reduce(
      (acc, r) => ({
        total: acc.total + (r.total_leads ?? 0),
        new_leads: acc.new_leads + (r.new_leads ?? 0),
        buyer_intent: acc.buyer_intent + (r.buyer_intent_leads ?? 0),
        won_leads: acc.won_leads + (r.won_leads ?? 0),
        outreached_leads: acc.outreached_leads + (r.outreached_leads ?? 0),
        saved_leads: acc.saved_leads + (r.saved_leads ?? 0),
      }),
      { total: 0, new_leads: 0, buyer_intent: 0, won_leads: 0, outreached_leads: 0, saved_leads: 0 },
    );
  })();

  const useServerCounts = !platformFilter && serverCounts !== null;
  const categoryCounts = useServerCounts && serverCounts
    ? {
        all: serverCounts.total,
        new: serverCounts.new_leads,
        buyer_intent: serverCounts.buyer_intent,
        saved: serverCounts.saved_leads,
        outreached: serverCounts.outreached_leads,
        won: serverCounts.won_leads,
        // Archived count still comes from loaded batch (only populated
        // when the user is actively on the Archived tab).
        archived: platformScoped.length,
      }
    : {
        all: platformScoped.length,
        new: platformScoped.filter((l) => (l.status ?? 'new') === 'new').length,
        buyer_intent: platformScoped.filter((l) => l.intent === 'buyer_intent').length,
        saved: platformScoped.filter((l) => l.saved === 1).length,
        outreached: platformScoped.filter(
          (l) => l.status === 'outreached' || l.outreached === 1,
        ).length,
        won: platformScoped.filter((l) => (l.status ?? '') === 'won').length,
        archived: platformScoped.length,
      };

  // Fetch the user's own profile (for sidebar role gating + stat cards).
  useEffect(() => {
    request<MeResponse>('/api/me')
      .then(setMe)
      .catch(() => {
        /* sidebar will fall back to showing all items */
      });
  }, [request]);

  // For owners + admin-VAs, pull the client list once so we can render the
  // dropdown. va_admin sees every client in the agency, same as the owner.
  useEffect(() => {
    if (
      me &&
      !me.user_not_linked &&
      (me.user.role === 'owner' || me.user.role === 'va_admin')
    ) {
      request<{ clients: ClientPickerRow[] }>('/admin/clients')
        .then((res) => setClientRows(res.clients))
        .catch(() => {
          /* fail silently — picker just stays hidden */
        });
    }
  }, [me, request]);

  // Post-mutation hook: re-fetch both counts sources that drive the
  // stat-row at the top of the page. Used after archive / restore so the
  // "Total" tile drops immediately instead of staying frozen at the
  // page-load value until a hard reload (reported 2026-05-26).
  const reloadCounts = useCallback(() => {
    request<MeResponse>('/api/me')
      .then(setMe)
      .catch(() => {
        /* keep previous me */
      });
    if (
      me &&
      !me.user_not_linked &&
      (me.user.role === 'owner' || me.user.role === 'va_admin')
    ) {
      request<{ clients: ClientPickerRow[] }>('/admin/clients')
        .then((res) => setClientRows(res.clients))
        .catch(() => {
          /* picker keeps stale rows; not worth surfacing */
        });
    }
  }, [me, request]);

  const loadPage = useCallback(
    async (before?: number) => {
      const qs = new URLSearchParams({ limit: '50' });
      if (before !== undefined) qs.set('before', String(before));
      if (pickedClient && pickedClient !== ALL_CLIENTS) {
        qs.set('client_account_id', pickedClient);
      }
      // Archived leads are excluded from the default response — the "Archived"
      // tab asks for them explicitly. Switching the tab changes loadPage's
      // identity, which refires the load effect below.
      if (archivedView) qs.set('archived', '1');
      // v3.14.1 — send the active tab filter to the server so it can
      // return matching rows sorted by detected_at DESC. Previously
      // client-side filtered only the loaded 50 leads, which showed empty
      // for tabs whose leads were older than that window.
      // 'all' and 'archived' are handled server-side already (no filter /
      // archived param), so we only send the specific-status filters.
      if (
        category === 'new' ||
        category === 'buyer_intent' ||
        category === 'saved' ||
        category === 'outreached' ||
        category === 'won'
      ) {
        qs.set('filter', category);
      }
      const data = await request<LeadsResponse>(`/api/me/leads?${qs}`);
      return data;
    },
    [request, pickedClient, archivedView, category],
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    loadPage()
      .then((d) => {
        setLeads(d.leads);
        setCursor(d.next_cursor);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [loadPage]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const d = await loadPage(cursor);
      setLeads((prev) => [...prev, ...d.leads]);
      setCursor(d.next_cursor);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  // Page forward. If another already-loaded page exists, just advance. If
  // we're at the end of what's loaded but the server still has more, fetch the
  // next batch first — loadMore appends, growing the filtered set — then step
  // the page. The render-time clamp keeps the page index in range either way.
  async function goNext() {
    const tp = Math.max(1, Math.ceil(visibleLeads.length / PAGE_SIZE));
    const cp = Math.min(page, tp - 1);
    if (cp < tp - 1) {
      setPage(cp + 1);
      return;
    }
    if (cursor && !loadingMore) {
      await loadMore();
      setPage((p) => p + 1);
    }
  }

  // Merge a single updated lead back into the list (called by LeadItem after
  // a PATCH succeeds). Keeps existing order — we don't re-sort by last_modified.
  //
  // Also triggers reloadCounts() because marking won/lost/saved/outreached
  // shifts the headline tiles ("New", "Won", "Buyer-intent") and those are
  // derived from /api/me + /admin/clients counts, not from the in-memory
  // leads list. Without this, the tiles stayed frozen after a status
  // change until a hard reload (same class of bug as the archive count
  // freeze fixed earlier in this session — full-stack audit H2).
  const onLeadUpdated = useCallback(
    (updated: LeadRow) => {
      setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      reloadCounts();
    },
    [reloadCounts],
  );

  // --- Multi-select + archive ----------------------------------------------
  // Archiving is a soft delete: the lead row stays in the database (the agency
  // keeps full history) but leaves the client's working list. The current view
  // shows exactly one set — active OR archived — so an archive/restore always
  // removes the affected rows from whatever's on screen.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveErr, setArchiveErr] = useState<string | null>(null);

  // Reset selection AND pagination whenever the dataset underneath them
  // changes — a tab switch, client switch, or platform filter starts the user
  // back at page 1 with a clean selection.
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(0);
  }, [category, pickedClient, platformFilter]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  async function archiveLeads(ids: string[], archived: boolean) {
    if (ids.length === 0) return;
    setArchiveBusy(true);
    setArchiveErr(null);
    try {
      await request<{ archived: boolean; updated: number }>(
        '/api/me/leads/archive',
        { method: 'POST', body: JSON.stringify({ ids, archived }) },
      );
      const idSet = new Set(ids);
      setLeads((prev) => prev.filter((l) => !idSet.has(l.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      // Refetch the per-client / agency-wide counts that drive the stat
      // row at the top of the page. Without this, archiving a lead
      // shrinks the visible list but the "Total" tile stays at the old
      // number until a hard reload (reported as a bug 2026-05-26).
      reloadCounts();
    } catch (e) {
      setArchiveErr((e as Error).message);
    } finally {
      setArchiveBusy(false);
    }
  }

  // --- Auto-analyze --------------------------------------------------------
  // When leads load, any that haven't been AI-classified yet get analyzed
  // automatically — one at a time, throttled, and capped per page load so we
  // never spike the client's AI rate limit. The extension also analyzes at
  // capture time; this is the safety net for anything that arrived raw.
  //
  // Concurrency model: a single runner loop, guarded by `runningRef`, reads
  // the latest leads from `leadsRef`. It deliberately does NOT cancel on
  // `leads` changes (every analyze result mutates `leads`) — re-renders just
  // hit the `runningRef` guard and no-op. It only stops on unmount.
  // `autoAnalyzedIds` is the claim set — every id added here is considered
  // "handled" (whether by the auto-runner or a manual click) and is never
  // auto-analyzed again. Its size is also the lifetime cap counter: once it
  // hits AUTO_ANALYZE_CAP the runner stops for good, so a big backlog can't
  // get chewed through 12-at-a-time across repeated re-runs.
  const autoAnalyzedIds = useRef<Set<string>>(new Set());
  const runningRef = useRef(false);
  const unmountedRef = useRef(false);
  const leadsRef = useRef<LeadRow[]>(leads);
  leadsRef.current = leads;
  const [autoAnalyzing, setAutoAnalyzing] = useState(false);
  const AUTO_ANALYZE_CAP = 12; // hard lifetime ceiling on auto-analyzed leads
  const AUTO_ANALYZE_GAP_MS = 1400; // spacing between calls

  // Manual Analyze (per-card) calls this to claim a lead so the background
  // runner won't also analyze it. Returns false if it was already claimed.
  const claimForAnalysis = useCallback((id: string): boolean => {
    if (autoAnalyzedIds.current.has(id)) return false;
    autoAnalyzedIds.current.add(id);
    return true;
  }, []);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (runningRef.current) return; // a run is already in flight
    // Don't burn AI budget classifying archived leads — they're off the
    // working list by definition.
    if (archivedView) return;
    // Lifetime cap — once we've claimed AUTO_ANALYZE_CAP leads, stop for good.
    if (autoAnalyzedIds.current.size >= AUTO_ANALYZE_CAP) return;
    const hasWork = leads.some(
      (l) => l.analyzed !== 1 && !autoAnalyzedIds.current.has(l.id),
    );
    if (!hasWork) return;

    runningRef.current = true;
    setAutoAnalyzing(true);

    (async () => {
      while (
        !unmountedRef.current &&
        autoAnalyzedIds.current.size < AUTO_ANALYZE_CAP
      ) {
        const next = leadsRef.current.find(
          (l) => l.analyzed !== 1 && !autoAnalyzedIds.current.has(l.id),
        );
        if (!next) break;
        autoAnalyzedIds.current.add(next.id); // claim up-front
        try {
          const res = await request<{ lead: LeadRow }>(
            `/api/me/leads/${next.id}/analyze`,
            { method: 'POST' },
          );
          if (!unmountedRef.current) onLeadUpdated(res.lead);
        } catch {
          // Rate-limited or transient — stop the run quietly; the manual
          // per-card Analyze button is still available.
          break;
        }
        await new Promise((r) => setTimeout(r, AUTO_ANALYZE_GAP_MS));
      }
      runningRef.current = false;
      if (!unmountedRef.current) setAutoAnalyzing(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, request, onLeadUpdated, archivedView]);

  // --- Auto-draft replies --------------------------------------------------
  // Gated behind the per-client `auto_draft_replies` feature flag. When on,
  // leads worth replying to — analyzed AND classified buyer_intent / question
  // — get their AI replies generated in the background and persisted, so a VA
  // opening the dashboard finds drafts already waiting. Same runner shape as
  // auto-analyze (single loop, claim-set dedup, hard per-load cap, throttled).
  // A lead with replies_json already set is never re-drafted, which keeps the
  // AI spend bounded. The human still reviews + posts — nothing auto-posts.
  const autoDraftEnabled =
    !!me &&
    !me.user_not_linked &&
    me.feature_flags?.['auto_draft_replies'] === true;
  const autoDraftedIds = useRef<Set<string>>(new Set());
  const draftRunningRef = useRef(false);
  const [autoDrafting, setAutoDrafting] = useState(false);
  const AUTO_DRAFT_CAP = 8; // hard lifetime ceiling per page load
  const AUTO_DRAFT_GAP_MS = 2200; // spacing between calls (replies are heavier)

  const claimForDraft = useCallback((id: string): boolean => {
    if (autoDraftedIds.current.has(id)) return false;
    autoDraftedIds.current.add(id);
    return true;
  }, []);

  useEffect(() => {
    if (!autoDraftEnabled) return;
    if (draftRunningRef.current) return;
    if (archivedView) return;
    if (autoDraftedIds.current.size >= AUTO_DRAFT_CAP) return;
    // A lead is draft-worthy once it's been analyzed and the AI classified it
    // as something worth a reply, and it has no drafts yet.
    const draftWorthy = (l: LeadRow) =>
      l.analyzed === 1 &&
      (l.intent === 'buyer_intent' || l.intent === 'question') &&
      !l.replies_json &&
      !autoDraftedIds.current.has(l.id);
    if (!leads.some(draftWorthy)) return;

    draftRunningRef.current = true;
    setAutoDrafting(true);

    (async () => {
      while (
        !unmountedRef.current &&
        autoDraftedIds.current.size < AUTO_DRAFT_CAP
      ) {
        const next = leadsRef.current.find(draftWorthy);
        if (!next) break;
        autoDraftedIds.current.add(next.id); // claim up-front
        try {
          const res = await request<{ replies: AiReplies; lead: LeadRow }>(
            `/api/me/leads/${next.id}/generate-replies`,
            { method: 'POST' },
          );
          if (!unmountedRef.current) onLeadUpdated(res.lead);
        } catch {
          // Rate-limited or transient — stop quietly; the manual per-card
          // "Generate replies" button still works.
          break;
        }
        await new Promise((r) => setTimeout(r, AUTO_DRAFT_GAP_MS));
      }
      draftRunningRef.current = false;
      if (!unmountedRef.current) setAutoDrafting(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, request, onLeadUpdated, archivedView, autoDraftEnabled]);

  const userRole = me && !me.user_not_linked ? me.user.role : undefined;

  // Stat tiles: when a specific client is picked, use that client's per-row
  // counts from /admin/clients. When "All clients" is picked, sum across all
  // visible clients (owner view) or fall back to /api/me counts.
  const counts = (() => {
    if (
      pickedClient !== ALL_CLIENTS &&
      clientRows &&
      clientRows.length > 0
    ) {
      const row = clientRows.find((r) => r.id === pickedClient);
      if (row) {
        return {
          total: row.total_leads,
          buyer_intent: row.buyer_intent_leads,
          new_leads: row.new_leads,
          won_leads: row.won_leads,
          outreached_leads: row.outreached_leads ?? 0,
          saved_leads: row.saved_leads ?? 0,
        };
      }
    }
    if (clientRows && clientRows.length > 0) {
      return clientRows.reduce(
        (acc, r) => ({
          total: acc.total + (r.total_leads ?? 0),
          buyer_intent: acc.buyer_intent + (r.buyer_intent_leads ?? 0),
          new_leads: acc.new_leads + (r.new_leads ?? 0),
          won_leads: acc.won_leads + (r.won_leads ?? 0),
          outreached_leads: acc.outreached_leads + (r.outreached_leads ?? 0),
          saved_leads: acc.saved_leads + (r.saved_leads ?? 0),
        }),
        { total: 0, buyer_intent: 0, new_leads: 0, won_leads: 0, outreached_leads: 0, saved_leads: 0 },
      );
    }
    return me && !me.user_not_linked
      ? { ...me.counts, outreached_leads: 0, saved_leads: 0 }
      : null;
  })();
  const showClientPicker =
    !!clientRows &&
    clientRows.length > 0 &&
    (userRole === 'owner' || userRole === 'va_admin');

  // --- Client-side pagination over the filtered list -----------------------
  // `page` can drift past the end if visibleLeads shrinks (e.g. archiving a
  // batch), so clamp it for every read. currentPage is the source of truth.
  const totalPages = Math.max(1, Math.ceil(visibleLeads.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedLeads = visibleLeads.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE,
  );
  const rangeStart = visibleLeads.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const rangeEnd = Math.min(visibleLeads.length, (currentPage + 1) * PAGE_SIZE);

  return (
    <AppShell {...(userRole ? { userRole } : {})}>
      <main className="page">
        {/* Page hero — colored band that mirrors the visual weight of the
            client-context bar on /admin/clients/:id, so the dashboard feels
            unified across pages. */}
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">⚡</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Leads</h1>
            <p className="page-hero-sub">
              Every Facebook &amp; Reddit match GroupPulse has captured for you.
              Use status + notes to triage; edits sync back to the extension in 60s.
            </p>
          </div>
        </div>

        {/* Client filter — owners only. Switching the dropdown refetches the
            lead list scoped to that client (or shows all leads on "All"). */}
        {showClientPicker && (
          <div className="leads-filter-bar">
            <label
              htmlFor="leads-client-picker"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--muted)',
                marginRight: 10,
              }}
            >
              Filter by client
            </label>
            <select
              id="leads-client-picker"
              value={pickedClient}
              onChange={(e) => setPickedClient(e.target.value)}
              className="leads-filter-select"
            >
              <option value={ALL_CLIENTS}>All clients (agency-wide)</option>
              {clientRows!.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.total_leads.toLocaleString()} leads
                </option>
              ))}
            </select>
            {pickedClient !== ALL_CLIENTS && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginLeft: 'auto', fontSize: 13, padding: '6px 12px' }}
                onClick={() => setPickedClient(ALL_CLIENTS)}
              >
                Clear filter
              </button>
            )}
          </div>
        )}

        {/* Quick stats — same visual style the admin client view uses */}
        {counts && (
          <div className="stat-row">
            <LeadStat label="Total" value={counts.total} accent="#4f46e5" />
            <LeadStat label="Buyer intent" value={counts.buyer_intent} accent="#10b981" />
            <LeadStat label="New" value={counts.new_leads} accent="#0284c7" />
            <LeadStat label="Won" value={counts.won_leads} accent="#f59e0b" />
          </div>
        )}

        {/* Category quick-filter tabs. The first five filter the loaded page
            client-side; "Archived" refetches (archived leads are excluded from
            the default response). The count badge only renders for tabs in the
            currently-loaded dataset — the active page can't know the archived
            count, and vice versa. */}
        {!loading && !error && (
          <div className="lead-tabs" role="tablist" aria-label="Filter leads">
            {(
              [
                { key: 'all', label: 'All' },
                { key: 'new', label: 'New' },
                { key: 'buyer_intent', label: 'Buyer Intent' },
                { key: 'saved', label: 'Saved' },
                { key: 'outreached', label: 'Outreached' },
                { key: 'won', label: 'Won' },
                { key: 'archived', label: 'Archived' },
              ] as const
            ).map((tab) => {
              const showBadge = archivedView
                ? tab.key === 'archived'
                : tab.key !== 'archived';
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={category === tab.key}
                  className={`lead-tab ${category === tab.key ? 'is-active' : ''}`}
                  onClick={() => setCategory(tab.key)}
                >
                  {tab.label}
                  {showBadge && (
                    <span className="lead-tab-count">
                      {categoryCounts[tab.key]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <section className="card">
          {loading ? (
            <p className="muted">Loading…</p>
          ) : error ? (
            <p style={{ color: '#b91c1c' }}>Error: {error}</p>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>
                {archivedView ? '🗂️' : '📭'}
              </div>
              <h2 style={{ margin: '0 0 6px' }}>
                {archivedView ? 'No archived leads' : 'No leads yet'}
              </h2>
              <p className="muted" style={{ margin: 0 }}>
                {archivedView
                  ? 'Leads you archive will show up here — you can restore them anytime.'
                  : 'As soon as your extension captures matches on Facebook or Reddit, they’ll land here.'}
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 4,
                }}
              >
                <p className="muted" style={{ margin: 0 }}>
                  {visibleLeads.length === 0
                    ? 'No leads to show.'
                    : `Showing ${rangeStart}–${rangeEnd} of ${
                        visibleLeads.length
                      }${cursor ? '+' : ''}${
                        archivedView ? ' archived' : ''
                      } ${visibleLeads.length === 1 ? 'lead' : 'leads'}.`}
                </p>
                {autoAnalyzing && !archivedView && (
                  <span className="auto-analyze-pill" title="AI is classifying new leads">
                    ✦ Auto-analyzing…
                  </span>
                )}
                {autoDrafting && !archivedView && (
                  <span
                    className="auto-analyze-pill"
                    title="AI is drafting replies for buyer-intent leads"
                  >
                    ✎ Auto-drafting replies…
                  </span>
                )}
                {platformFilter && (
                  <button
                    type="button"
                    onClick={clearPlatformFilter}
                    className="leads-platform-chip"
                    title="Clear platform filter"
                  >
                    {platformFilter === 'reddit' ? 'Reddit' : 'Facebook'} only ✕
                  </button>
                )}
                {pagedLeads.length > 0 && (
                  <button
                    type="button"
                    className="link-button"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                      const allSelected = pagedLeads.every((l) =>
                        selectedIds.has(l.id),
                      );
                      setSelectedIds(
                        allSelected
                          ? new Set()
                          : new Set(pagedLeads.map((l) => l.id)),
                      );
                    }}
                  >
                    {pagedLeads.every((l) => selectedIds.has(l.id))
                      ? 'Deselect page'
                      : 'Select page'}
                  </button>
                )}
              </div>

              {/* Bulk action bar — appears once at least one lead is checked.
                  In the active list it archives; in the Archived tab it
                  restores. Either way the affected rows leave the view. */}
              {selectedIds.size > 0 && (
                <div className="lead-bulk-bar">
                  <span className="lead-bulk-count">
                    {selectedIds.size} selected
                  </span>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={archiveBusy}
                    onClick={() =>
                      void archiveLeads([...selectedIds], !archivedView)
                    }
                  >
                    {archiveBusy
                      ? 'Working…'
                      : archivedView
                        ? `Restore ${selectedIds.size}`
                        : `Archive ${selectedIds.size}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={archiveBusy}
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </button>
                  {archiveErr && (
                    <span style={{ color: '#b91c1c', fontSize: 13 }}>
                      {archiveErr}
                    </span>
                  )}
                </div>
              )}

              {visibleLeads.length === 0 ? (
                <p className="muted" style={{ fontStyle: 'italic' }}>
                  No leads in the loaded page match the current filter.{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setCategory('all');
                      if (platformFilter) clearPlatformFilter();
                    }}
                    className="link-button"
                  >
                    Show all
                  </button>
                </p>
              ) : null}
              <ul className="lead-list">
                {pagedLeads.map((lead) => (
                  <LeadItem
                    key={lead.id}
                    lead={lead}
                    onUpdated={onLeadUpdated}
                    claimForAnalysis={claimForAnalysis}
                    claimForDraft={claimForDraft}
                    selected={selectedIds.has(lead.id)}
                    onToggleSelect={toggleSelect}
                    onArchive={(id, archived) =>
                      void archiveLeads([id], archived)
                    }
                  />
                ))}
              </ul>

              {/* Page controls. Shown whenever there's more than one page —
                  or the server still has older batches to pull. "Next" past
                  the last loaded page fetches the next batch first. */}
              {(totalPages > 1 || cursor) && (
                <div className="lead-pager">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentPage === 0}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="lead-pager-status">
                    Page {currentPage + 1} of {totalPages}
                    {cursor ? '+' : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={
                      loadingMore || (currentPage >= totalPages - 1 && !cursor)
                    }
                    onClick={() => void goNext()}
                  >
                    {loadingMore ? 'Loading…' : 'Next →'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </AppShell>
  );
}

// Small inline brand icon. Uses the actual platform logo glyphs so a quick
// glance tells you where the lead came from — no reading required.
function PlatformPill({ platform }: { platform?: string | null }) {
  if (platform === 'reddit') {
    return (
      <span
        aria-label="Reddit"
        title="Captured from Reddit"
        style={iconPillStyle('#FF4500', '#ffffff')}
      >
        {/* Reddit "snoo" alien — simplified inline SVG */}
        <svg width="13" height="13" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M10 0a10 10 0 1 0 0 20A10 10 0 0 0 10 0Zm5.36 11.32a1.46 1.46 0 0 1-1.5 1.46c-.36 0-.7-.13-.96-.34-.84.55-1.94.88-3.16.92l.6-2.66 1.86.4c.04.42.4.74.84.74.46 0 .84-.38.84-.84a.84.84 0 0 0-1.66-.18l-2.04-.42a.22.22 0 0 0-.26.16l-.66 3.06c-1.2-.06-2.28-.39-3.1-.94-.26.21-.6.34-.96.34a1.46 1.46 0 0 1-1.5-1.46c0-.62.42-1.16 1-1.36a4 4 0 0 1-.04-.5c0-1.86 2.34-3.38 5.22-3.38 2.88 0 5.22 1.52 5.22 3.38 0 .18-.02.34-.04.5a1.43 1.43 0 0 1 1 1.36ZM7.9 9.8c.42 0 .76.38.76.84 0 .46-.34.84-.76.84-.42 0-.76-.38-.76-.84 0-.46.34-.84.76-.84Zm4.96.84c0-.46.34-.84.76-.84.42 0 .76.38.76.84 0 .46-.34.84-.76.84a.79.79 0 0 1-.76-.84Z"
          />
        </svg>
      </span>
    );
  }
  // Facebook (default for legacy null platform)
  return (
    <span
      aria-label="Facebook"
      title="Captured from Facebook"
      style={iconPillStyle('#1877F2', '#ffffff')}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396v8.01Z"
        />
      </svg>
    </span>
  );
}
function iconPillStyle(bg: string, fg: string): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 999,
    background: bg,
    color: fg,
    flexShrink: 0,
  };
}

// Standalone stat tile with a colored accent stripe — used in the row above
// the lead list.
function LeadStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <div className="lead-stat" style={{ borderTopColor: accent }}>
      <div className="lead-stat-label">{label}</div>
      <div className="lead-stat-value">{value.toLocaleString()}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// LeadItem — single row with inline status / outreached / notes editing.
// -----------------------------------------------------------------------------

type LeadUpdatePayload = {
  status?: string;
  notes?: string | null;
  outreached?: boolean;
  saved?: boolean;
};

// Workflow statuses. 'archived' was removed here once the dedicated Archive
// feature shipped — archiving a lead is now its own action (the 🗄 Archive
// button + the Archived tab), independent of workflow status. The backend
// still accepts 'archived' for back-compat, and the <select> below defensively
// re-adds any non-standard status a lead might already carry.
const STATUS_OPTIONS = ['new', 'outreached', 'won', 'lost'] as const;

// AI reply variants returned by POST /api/me/leads/:id/generate-replies.
type AiReplies = {
  value_add: string | null;
  promotional: string | null;
};

// Parse a lead's persisted replies_json blob into the AiReplies shape.
// Returns null when there are no drafted replies (or the blob is corrupt).
function parseRepliesJson(raw: string | null | undefined): AiReplies | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<AiReplies>;
    if (!p || typeof p !== 'object') return null;
    const value_add = typeof p.value_add === 'string' ? p.value_add : null;
    const promotional =
      typeof p.promotional === 'string' ? p.promotional : null;
    if (!value_add && !promotional) return null;
    return { value_add, promotional };
  } catch {
    return null;
  }
}

export function LeadItem({
  lead,
  onUpdated,
  claimForAnalysis,
  claimForDraft,
  selected,
  onToggleSelect,
  onArchive,
}: {
  lead: LeadRow;
  onUpdated: (lead: LeadRow) => void;
  /** Optional — when present (Leads page), claims the lead so the
   *  background auto-analyzer won't also process it. */
  claimForAnalysis?: (id: string) => boolean;
  /** Optional — claims the lead so the background auto-draft runner won't
   *  also generate replies for it. */
  claimForDraft?: (id: string) => boolean;
  /** Optional — multi-select state for the bulk archive bar. */
  selected?: boolean;
  /** Optional — toggles this lead's selection. When omitted, no checkbox. */
  onToggleSelect?: (id: string) => void;
  /** Optional — archive (true) or restore (false) this lead. When omitted,
   *  no Archive/Restore button (e.g. the admin per-client view). */
  onArchive?: (id: string, archived: boolean) => void;
}) {
  const { request } = useApi();
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saving, setSaving] = useState<
    'status' | 'notes' | 'outreached' | 'saved' | null
  >(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // AI replies — PERSISTED on the lead row (replies_json). Derived straight
  // from the lead prop so a draft made by the background auto-draft runner,
  // or on another device, shows up here without any local state juggling.
  const replies = useMemo(
    () => parseRepliesJson(lead.replies_json),
    [lead.replies_json],
  );
  const [generatingReplies, setGeneratingReplies] = useState(false);
  const [repliesErr, setRepliesErr] = useState<string | null>(null);
  const [copiedVariant, setCopiedVariant] = useState<string | null>(null);

  // AI intent analysis — PERSISTED. Click → classify → the result
  // (intent / confidence / summary / analyzed) is saved on the lead row
  // and merged back via onUpdated so the pill + summary stick.
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState<string | null>(null);

  async function analyze() {
    if (analyzing) return;
    // Claim this lead so the background auto-analyzer skips it — prevents a
    // double API call (and double cost) if the runner reaches it too.
    // claimForAnalysis returns false if it was already claimed/handled; we
    // still proceed (the user explicitly clicked — likely a re-analyze).
    claimForAnalysis?.(lead.id);
    setAnalyzing(true);
    setAnalyzeErr(null);
    try {
      const res = await request<{ lead: LeadRow }>(
        `/api/me/leads/${lead.id}/analyze`,
        { method: 'POST' },
      );
      onUpdated(res.lead);
    } catch (e) {
      setAnalyzeErr((e as Error).message);
    } finally {
      setAnalyzing(false);
    }
  }

  async function generateReplies() {
    if (generatingReplies) return;
    // Claim the lead so the background auto-draft runner skips it — prevents
    // a double generate (and double AI cost) on the same lead.
    claimForDraft?.(lead.id);
    setGeneratingReplies(true);
    setRepliesErr(null);
    try {
      // The endpoint persists replies_json and returns the full updated lead;
      // merging it back makes the reply cards render from the lead prop.
      const res = await request<{ replies: AiReplies; lead: LeadRow }>(
        `/api/me/leads/${lead.id}/generate-replies`,
        { method: 'POST' },
      );
      onUpdated(res.lead);
    } catch (e) {
      setRepliesErr((e as Error).message);
    } finally {
      setGeneratingReplies(false);
    }
  }

  async function copyReply(text: string, variant: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedVariant(variant);
      window.setTimeout(() => setCopiedVariant(null), 1800);
    } catch {
      /* clipboard blocked — user can still select-copy manually */
    }
  }

  // Debounced notes save. Hold typing for 1s before firing PATCH so we don't
  // hammer the API on every keystroke.
  const notesTimer = useRef<number | null>(null);
  useEffect(() => {
    if (notes === (lead.notes ?? '')) return; // no real change
    if (notesTimer.current) window.clearTimeout(notesTimer.current);
    notesTimer.current = window.setTimeout(() => {
      void patch({ notes: notes || null }, 'notes');
    }, 1000);
    return () => {
      if (notesTimer.current) window.clearTimeout(notesTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  async function patch(
    payload: LeadUpdatePayload,
    label: 'status' | 'notes' | 'outreached' | 'saved',
  ) {
    setSaving(label);
    setErr(null);
    try {
      const res = await request<{ lead: LeadRow }>(
        `/api/me/leads/${lead.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
      );
      onUpdated(res.lead);
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 2000);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(null);
    }
  }

  const time = formatRelativeTime(lead.detected_at);
  const intent = lead.intent ?? 'unscored';
  const intentColor =
    intent === 'buyer_intent'
      ? '#15803d'
      : intent === 'research'
        ? '#0284c7'
        : '#64748b';
  const intentBg =
    intent === 'buyer_intent'
      ? '#d1fae5'
      : intent === 'research'
        ? '#dbeafe'
        : '#f1f5f9';

  // Parse the matched keywords from the JSON column. These are the exact
  // phrases the extension saw in the post that caused us to capture it.
  const matchedKeywords: string[] = (() => {
    if (!lead.keywords_json) return [];
    try {
      const parsed = JSON.parse(lead.keywords_json);
      return Array.isArray(parsed)
        ? parsed.filter((x): x is string => typeof x === 'string')
        : [];
    } catch {
      return [];
    }
  })();

  const [expanded, setExpanded] = useState(false);
  const fullText = lead.post_text || '';
  const showTruncated = fullText.length > 320 && !expanded;
  const displayText = showTruncated ? fullText.slice(0, 320) + '…' : fullText;

  return (
    <li className="lead-card">
      {/* Header: author, group, time, intent pill */}
      <div className="lead-card-header">
        {onToggleSelect && (
          <input
            type="checkbox"
            className="lead-select-checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(lead.id)}
            aria-label={selected ? 'Deselect lead' : 'Select lead'}
            title={selected ? 'Deselect' : 'Select'}
          />
        )}
        {/* flex:1 so the author/meta block fills the row and stays
            left-aligned. Without it, the header's space-between centers this
            block once the select checkbox makes it a 3-item flex row. */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="lead-card-author">
            {lead.author_url ? (
              <a href={lead.author_url} target="_blank" rel="noreferrer">
                {lead.author_name ?? 'Unknown author'}
              </a>
            ) : (
              lead.author_name ?? 'Unknown author'
            )}
          </div>
          <div className="lead-card-meta">
            {/* Client chip — owner/va_admin views typically span multiple
                clients, so the lead's client is the first thing they need
                to know. Clicks through to the per-client detail page.
                Renders only when the API returned client_account_name
                (always true for the current /api/me/leads response). */}
            {lead.client_account_name && (
              <>
                <Link
                  to={`/admin/clients/${lead.client_account_id}`}
                  className="lead-card-client-chip"
                  title={`View ${lead.client_account_name}'s profile`}
                >
                  {lead.client_account_name}
                </Link>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span title={new Date(lead.detected_at).toLocaleString()}>{time}</span>
            <span aria-hidden="true">·</span>
            {/* Platform icon doubles as a quick jump to the original post. */}
            {lead.post_url ? (
              <a
                href={lead.post_url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open this ${
                  (lead.platform ?? 'facebook') === 'reddit' ? 'Reddit' : 'Facebook'
                } post`}
                style={{ display: 'inline-flex' }}
              >
                <PlatformPill platform={lead.platform ?? null} />
              </a>
            ) : (
              <PlatformPill platform={lead.platform ?? null} />
            )}
            <span aria-hidden="true">·</span>
            <span>
              {lead.group_url ? (
                <a href={lead.group_url} target="_blank" rel="noreferrer">
                  {lead.group_name ?? 'Unknown group'}
                </a>
              ) : (
                lead.group_name ?? 'Unknown group'
              )}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Save star — toggles lead.saved via PATCH. */}
          <button
            type="button"
            className="lead-save-star"
            aria-pressed={lead.saved === 1}
            disabled={saving === 'saved'}
            title={lead.saved === 1 ? 'Unsave lead' : 'Save lead'}
            onClick={() => void patch({ saved: lead.saved !== 1 }, 'saved')}
            style={{ color: lead.saved === 1 ? '#f59e0b' : 'var(--muted)' }}
          >
            {lead.saved === 1 ? '★' : '☆'}
          </button>
          <span
            className="lead-card-intent"
            style={{ color: intentColor, background: intentBg }}
          >
            {intent.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Matched keyword chips (the actual reason we surfaced this) */}
      {matchedKeywords.length > 0 && (
        <div className="lead-card-chips">
          {matchedKeywords.map((kw, i) => (
            <span key={i} className="lead-chip">
              {kw}
            </span>
          ))}
        </div>
      )}

      {/* Post text with matched keywords highlighted, same as extension */}
      <div
        className="lead-card-body"
        dangerouslySetInnerHTML={{
          __html: highlightKeywords(displayText, matchedKeywords),
        }}
      />
      {fullText.length > 320 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--primary)',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            marginTop: 4,
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      {/* AI summary block (only when Claude has analysed it) */}
      {lead.summary && (
        <div className="lead-card-ai">
          <div className="lead-card-ai-label">✦ AI summary</div>
          <p>{lead.summary}</p>
          {lead.confidence !== null && lead.confidence !== undefined && (
            <div className="lead-card-ai-confidence">
              Confidence: {Math.round((lead.confidence || 0) * 100)}%
            </div>
          )}
        </div>
      )}

      {/* Inline editing strip */}
      <div className="lead-card-actions">
        <label className="lead-card-action">
          <span className="muted">Status:</span>
          <select
            value={lead.status ?? 'new'}
            disabled={saving === 'status'}
            onChange={(e) => void patch({ status: e.target.value }, 'status')}
          >
            {(STATUS_OPTIONS.includes(
              (lead.status ?? 'new') as (typeof STATUS_OPTIONS)[number],
            )
              ? ([...STATUS_OPTIONS] as string[])
              : [...STATUS_OPTIONS, lead.status as string]
            ).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="lead-card-action">
          <input
            type="checkbox"
            checked={lead.outreached === 1}
            disabled={saving === 'outreached'}
            onChange={(e) =>
              void patch({ outreached: e.target.checked }, 'outreached')
            }
          />
          <span>Outreached</span>
        </label>

        <button
          type="button"
          className="lead-card-action lead-ai-btn"
          onClick={() => void analyze()}
          disabled={analyzing}
          title="Have AI classify this post's intent"
        >
          {analyzing
            ? 'Analyzing…'
            : lead.analyzed === 1
              ? '✦ Re-analyze'
              : '✦ Analyze'}
        </button>

        <button
          type="button"
          className="lead-card-action lead-ai-btn"
          onClick={() => void generateReplies()}
          disabled={generatingReplies}
        >
          {generatingReplies
            ? 'Generating…'
            : replies
              ? '✦ Regenerate replies'
              : '✦ AI suggested replies'}
        </button>

        {lead.post_url && (
          <a
            className="lead-card-action lead-card-link"
            href={lead.post_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on{' '}
            {(lead.platform ?? 'facebook') === 'reddit' ? 'Reddit' : 'Facebook'} ↗
          </a>
        )}

        {/* Archive / Restore — soft delete. Hidden where no handler is passed
            (e.g. the admin per-client view). */}
        {onArchive && (
          <button
            type="button"
            className="lead-card-action lead-archive-btn"
            onClick={() => onArchive(lead.id, lead.archived !== 1)}
            title={
              lead.archived === 1
                ? 'Restore this lead to your active list'
                : 'Archive — hides it from your list, keeps the record'
            }
          >
            {lead.archived === 1 ? '♻ Restore' : '🗄 Archive'}
          </button>
        )}
      </div>

      {analyzeErr && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>
          Couldn&apos;t analyze: {analyzeErr}
        </div>
      )}

      {/* AI suggested replies — value-based + self-promotion variants. */}
      {repliesErr && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>
          Couldn&apos;t generate replies: {repliesErr}
        </div>
      )}
      {replies && (
        <div className="reply-suite">
          {replies.value_add && (
            <div className="reply-card reply-card-value">
              <div className="reply-card-head">
                <span className="reply-card-label">Value-Based Reply</span>
                <button
                  type="button"
                  className="reply-copy-btn"
                  onClick={() =>
                    void copyReply(replies.value_add!, 'value_add')
                  }
                >
                  {copiedVariant === 'value_add' ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <p className="reply-card-body">{replies.value_add}</p>
            </div>
          )}
          {replies.promotional && (
            <div className="reply-card reply-card-promo">
              <div className="reply-card-head">
                <span className="reply-card-label">Self-Promotion Reply</span>
                <button
                  type="button"
                  className="reply-copy-btn"
                  onClick={() =>
                    void copyReply(replies.promotional!, 'promotional')
                  }
                >
                  {copiedVariant === 'promotional' ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <p className="reply-card-body">{replies.promotional}</p>
            </div>
          )}
        </div>
      )}

      <textarea
        placeholder="Notes — what was the conversation, where are they in the buying cycle, what's next…"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{
          width: '100%',
          marginTop: 10,
          padding: '8px 10px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 14,
          fontFamily: 'inherit',
          minHeight: 60,
          resize: 'vertical',
          background: '#fff',
          color: 'var(--text)',
        }}
      />

      <div style={{ minHeight: 18, marginTop: 4, fontSize: 12 }}>
        {err ? (
          <span style={{ color: '#b91c1c' }}>Save failed: {err}</span>
        ) : saving ? (
          <span className="muted">Saving…</span>
        ) : savedAt ? (
          <span style={{ color: '#10b981' }}>Saved ✓</span>
        ) : null}
      </div>
    </li>
  );
}
